/**
 * GA4 / GTM dataLayer helpers (client-side).
 *
 * GTM listens for `dataLayer.push()` and forwards events to GA4.
 */

function getDataLayer() {
  window.dataLayer = window.dataLayer || [];
  return window.dataLayer;
}

/**
 * Clear previous ecommerce data so GTM/GA4 does not merge item data across events.
 *
 * @see https://developers.google.com/tag-platform/tag-manager/ecommerce
 */
export function clearEcommerceData() {
  getDataLayer().push({ ecommerce: null });
}

/**
 * Push any analytics event (non-ecommerce or custom payloads).
 *
 * @param {string} eventName GA4 event name, e.g. "search"
 * @param {Record<string, unknown>} [data={}] Additional top-level keys
 */
export function pushAnalyticsEvent(eventName, data = {}) {
  getDataLayer().push({
    event: eventName,
    ...data,
  });
}

/**
 * Push a GA4 recommended ecommerce event with an `ecommerce` object.
 *
 * @param {string} eventName e.g. "view_item", "select_item", "begin_checkout"
 * @param {Record<string, unknown>} ecommercePayload Usually `{ items: [...] }` plus optional value/currency
 * @param {Record<string, unknown>} [extraParams={}] Additional top-level keys (e.g. check_in on dates_selected)
 */
export function pushAnalyticsEcommerceEvent(
  eventName,
  ecommercePayload,
  extraParams = {},
) {
  clearEcommerceData();
  getDataLayer().push({
    event: eventName,
    ecommerce: ecommercePayload,
    ...extraParams,
  });
}

/**
 * select_item — click on an accommodation card link.
 */
export function initSelectItemTracking() {
  document.addEventListener("click", (event) => {
    const link = event.target.closest("[data-analytics-select-item]");
    if (!link) {
      return;
    }

    const raw = link.getAttribute("data-analytics-select-item");
    if (!raw) {
      return;
    }

    try {
      const ecommerce = JSON.parse(raw);
      pushAnalyticsEcommerceEvent("select_item", ecommerce);
    } catch {
      // Invalid JSON — skip.
    }
  });
}

initSelectItemTracking();

/**
 * header_cta_click — header "Book" link (.page-header__button).
 */
export function initHeaderCtaTracking() {
  document.addEventListener("click", (event) => {
    const link = event.target.closest(".page-header__button");
    if (!link || !(link instanceof HTMLAnchorElement)) {
      return;
    }

    pushAnalyticsEvent("header_cta_click");
  });
}

initHeaderCtaTracking();
